<template>
  <div class="submit container">
    <!-- ── Not authenticated: friendly gate ─────────────────────── -->
    <section v-if="!authed" class="gate reveal">
      <div class="gate__peel">🍌</div>
      <p class="eyebrow">Proposer une blague</p>
      <h1 class="gate__title">Connecte-toi pour proposer ta perle</h1>
      <p class="gate__lead">
        Les meilleures blagues méritent une signature. Rejoins la bande pour
        déposer la tienne et récolter les votes.
      </p>
      <BananaButton to="/login?redirect=/proposer" variant="primary" size="lg">
        Se connecter 🚀
      </BananaButton>
    </section>

    <!-- ── Success state ────────────────────────────────────────── -->
    <section v-else-if="submitted" class="done reveal">
      <div class="rain" aria-hidden="true">
        <span v-for="n in 14" :key="n" :style="rainStyle(n)">🍌</span>
      </div>
      <div class="done__badge">🎉</div>
      <h1 class="done__title">C'est dans la corbeille&nbsp;!</h1>
      <p class="done__lead">
        Ta blague est en ligne. Que le meilleur régime gagne.
      </p>

      <JokeCard v-if="createdJoke" :joke="createdJoke" class="done__card" />

      <div class="done__actions">
        <BananaButton variant="primary" size="lg" @click="reset">
          Proposer une autre ✍️
        </BananaButton>
        <BananaButton to="/" variant="outline" size="lg">
          Voir toutes les blagues
        </BananaButton>
      </div>
    </section>

    <!-- ── The form ─────────────────────────────────────────────── -->
    <section v-else class="form-wrap">
      <header class="form-head reveal">
        <p class="eyebrow">Proposer une blague</p>
        <h1 class="form-head__title">Balance ta banane 🍌</h1>
        <p class="form-head__lead">
          Une bonne vanne, quelques tags, et hop&nbsp;: la communauté jugera.
          Salut&nbsp;<strong>{{ pseudo }}</strong>&nbsp;!
        </p>
      </header>

      <form class="card reveal" @submit.prevent="onSubmit">
        <!-- Text + edito -->
        <div class="text-row">
          <label class="field text-row__field">
            <span class="field__label">Ta blague</span>
            <textarea
              v-model="text"
              class="field__area"
              :maxlength="MAX"
              rows="6"
              placeholder="Ex : C'est l'histoire d'une banane qui rentre dans un bar…"
            ></textarea>
            <span class="counter" :class="{ 'counter--warn': text.length > MAX - 60 }">
              {{ text.length }} / {{ MAX }}
            </span>
          </label>

          <!-- Editorial: peut-on rire de tout ? -->
          <aside class="edito" aria-label="Conseil sur le choix de la cible">
            <p class="edito__body">
              <strong>💡 Avant de l'envoyer…</strong> Si ta blague tape sur un
              groupe bien réel (blondes, belges, une religion, une origine…),
              deux secondes de recul&nbsp;: est-ce qu'elle gagne vraiment à viser
              ces gens-là&nbsp;? Souvent le même ressort marche mieux sur une
              cible fictive, absurde ou retournée. Rien d'interdit ici,
              juste&nbsp;: choisis ta cible et tes mots exprès.
            </p>
            <a
              class="edito__link"
              href="/peut-on-rire-de-tout"
              target="_blank"
              rel="noopener"
            >
              Pourquoi on te dit ça&nbsp;? ↗
            </a>
          </aside>
        </div>

        <!-- Tags grouped by axis -->
        <div class="field">
          <span class="field__label">Ses tags</span>
          <p class="field__hint">
            Coche au moins un <strong>ton</strong>, ajoute des thèmes si tu veux.
          </p>

          <div v-for="group in tagGroups" :key="group.axis" class="tag-group">
            <span class="tag-group__title">{{ group.title }}</span>
            <div class="pills">
              <label
                v-for="tag in group.tags"
                :key="tag.slug"
                class="pill"
                :class="{ 'pill--on': selected.includes(tag.slug), 'pill--sensitive': tag.isSensitive }"
              >
                <input
                  type="checkbox"
                  class="pill__input"
                  :value="tag.slug"
                  :checked="selected.includes(tag.slug)"
                  @change="toggleTag(tag.slug)"
                />
                <span class="pill__check" aria-hidden="true">
                  {{ selected.includes(tag.slug) ? '✓' : '' }}
                </span>
                {{ tag.label }}
                <span v-if="tag.isSensitive" class="pill__flag" aria-hidden="true">⚠️</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Error -->
        <p v-if="error" class="error">⚠️ {{ error }}</p>

        <BananaButton
          type="submit"
          variant="primary"
          size="lg"
          block
          :loading="loading"
          :disabled="!canSubmit"
        >
          Publier ma blague 🚀
        </BananaButton>
      </form>
    </section>
  </div>
</template>

<script>
import BananaButton from '@/components/ui/BananaButton.vue'
import JokeCard from '@/components/JokeCard.vue'
import { TAGS_QUERY, SUBMIT_JOKE_MUTATION } from '@/graphql/jokes'
import { isAuthenticated, authState } from '@/auth'

const AXIS_TITLES = {
  ton: 'Ton',
  theme: 'Thème',
}
const AXIS_ORDER = ['ton', 'theme']

export default {
  name: 'SubmitView',
  components: { BananaButton, JokeCard },
  data() {
    return {
      MAX: 10000,
      text: '',
      selected: [],
      tags: [],
      loading: false,
      error: '',
      submitted: false,
      createdJoke: null,
      authState,
    }
  },
  apollo: {
    tags: {
      query: TAGS_QUERY,
      update: (data) => data.tags || [],
      error() { /* keep empty list on failure */ },
    },
  },
  computed: {
    authed() {
      return isAuthenticated()
    },
    pseudo() {
      const u = this.authState.user
      return (u && (u.displayName || u.username || u.email)) || 'toi'
    },
    tagGroups() {
      const byAxis = {}
      for (const tag of this.tags || []) {
        const axis = tag.axis || 'autre'
        ;(byAxis[axis] = byAxis[axis] || []).push(tag)
      }
      const axes = Object.keys(byAxis).sort((a, b) => {
        const ia = AXIS_ORDER.indexOf(a)
        const ib = AXIS_ORDER.indexOf(b)
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
      })
      return axes.map((axis) => ({
        axis,
        title: AXIS_TITLES[axis] || axis.charAt(0).toUpperCase() + axis.slice(1),
        tags: byAxis[axis],
      }))
    },
    hasTon() {
      // At least one selected tag must belong to the "ton" axis.
      const tonSlugs = new Set(
        (this.tags || [])
          .filter((t) => (t.axis || '').toLowerCase() === 'ton')
          .map((t) => t.slug),
      )
      return this.selected.some((slug) => tonSlugs.has(slug))
    },
    canSubmit() {
      return this.text.trim().length > 0 && this.hasTon && !this.loading
    },
  },
  methods: {
    toggleTag(slug) {
      const i = this.selected.indexOf(slug)
      if (i === -1) this.selected.push(slug)
      else this.selected.splice(i, 1)
    },
    rainStyle(n) {
      // deterministic spread so SSR/hydration-safe and no random import
      const left = (n * 6.7) % 100
      const delay = (n % 7) * 0.18
      const dur = 2.6 + (n % 5) * 0.35
      const size = 1 + ((n % 4) * 0.25)
      return {
        left: `${left}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${dur}s`,
        fontSize: `${size}rem`,
      }
    },
    async onSubmit() {
      if (!this.canSubmit) {
        if (!this.text.trim()) this.error = 'Il faut écrire une blague, banane !'
        return
      }
      this.loading = true
      this.error = ''
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SUBMIT_JOKE_MUTATION,
          variables: { text: this.text.trim(), tagSlugs: this.selected },
        })
        this.createdJoke = data?.submitJoke?.joke || null
        this.submitted = true
      } catch (e) {
        this.error =
          e?.graphQLErrors?.[0]?.message || e?.message || "Oups, ça a glissé sur une peau de banane."
      } finally {
        this.loading = false
      }
    },
    reset() {
      this.text = ''
      this.selected = []
      this.error = ''
      this.submitted = false
      this.createdJoke = null
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
  },
}
</script>

<style scoped>
.submit {
  padding: 48px 20px 80px;
  min-height: 60vh;
}

/* ── Gate (logged out) ─────────────────────────────── */
.gate {
  max-width: 560px;
  margin: 40px auto 0;
  text-align: center;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-pop);
  padding: 48px 32px;
}
.gate__peel {
  font-size: 3.5rem;
  animation: swing 2.4s var(--ease-bounce) infinite;
  display: inline-block;
}
.gate__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(1.6rem, 4vw, 2.2rem);
  color: var(--ink-900);
  margin: 12px 0 10px;
}
.gate__lead {
  font-family: var(--font-body);
  color: var(--ink-500);
  margin: 0 auto 28px;
  max-width: 40ch;
  line-height: 1.5;
}

/* ── Form head ─────────────────────────────────────── */
.form-wrap { max-width: 720px; margin: 0 auto; }
.form-head { text-align: center; margin-bottom: 26px; }
.form-head__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: var(--ink-900);
  margin: 6px 0 8px;
}
.form-head__lead { font-family: var(--font-body); color: var(--ink-500); margin: 0; }
.form-head__lead strong { color: var(--banana-700); }

/* ── Card / form ───────────────────────────────────── */
.card {
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-pop);
  padding: 28px 26px 30px;
  display: flex;
  flex-direction: column;
  gap: 26px;
}
.field { display: flex; flex-direction: column; position: relative; }
.field__label {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--ink-900);
  margin-bottom: 10px;
}
.field__hint {
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--ink-500);
  margin: 0 0 14px;
}
.field__hint strong { color: var(--banana-700); }
.field__area {
  font-family: var(--font-body);
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--ink-900);
  background: var(--banana-50);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
  padding: 14px 16px;
  resize: vertical;
  transition: box-shadow 0.15s var(--ease-out), border-color 0.15s;
  outline: none;
}
.field__area::placeholder { color: var(--ink-500); opacity: 0.7; }
.field__area:focus {
  box-shadow: 0 0 0 4px var(--banana-300);
  border-color: var(--banana-700);
}
.counter {
  align-self: flex-end;
  margin-top: 6px;
  font-family: var(--font-body);
  font-size: 0.82rem;
  color: var(--ink-500);
}
.counter--warn { color: var(--bruise); font-weight: 700; }

/* ── Text + edito layout ───────────────────────────── */
.text-row {
  display: grid;
  grid-template-columns: 1fr minmax(220px, 260px);
  gap: 20px;
  align-items: start;
}
.text-row__field { min-width: 0; }
.edito {
  background: var(--banana-100);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
  box-shadow: 3px 3px 0 var(--border-c);
  padding: 16px 18px;
  align-self: stretch;
}
.edito__body {
  font-family: var(--font-body);
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--ink-900);
  margin: 0 0 12px;
}
.edito__body strong { color: var(--banana-700); }
.edito__link {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.88rem;
  color: var(--ink-900);
  text-decoration: none;
  border-bottom: 2px solid var(--banana-500);
  transition: color 0.15s, border-color 0.15s;
}
.edito__link:hover { color: var(--banana-700); border-color: var(--banana-700); }

/* ── Tag groups ────────────────────────────────────── */
.tag-group { margin-top: 4px; }
.tag-group + .tag-group { margin-top: 18px; }
.tag-group__title {
  display: block;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-500);
  margin-bottom: 9px;
}
.pills { display: flex; flex-wrap: wrap; gap: 10px; }
.pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--ink-900);
  background: var(--cream);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  padding: 8px 15px;
  cursor: pointer;
  user-select: none;
  transition: transform 0.12s var(--ease-bounce), box-shadow 0.12s, background 0.15s;
}
.pill:hover { transform: translateY(-2px); box-shadow: 3px 3px 0 var(--border-c); }
.pill--on {
  background: var(--banana-500);
  box-shadow: 3px 3px 0 var(--border-c);
  color: var(--ink-900);
}
.pill--sensitive.pill--on { background: var(--bruise); color: #fff; }
.pill__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}
.pill__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-c);
  border-radius: 5px;
  background: var(--surface);
  font-size: 0.8rem;
  font-weight: 900;
  line-height: 1;
}
.pill--on .pill__check { background: var(--ink-900); color: var(--banana-400); }
.pill--sensitive.pill--on .pill__check { color: #fff; }
.pill__flag { font-size: 0.85rem; }
.pill__input:focus-visible + .pill__check {
  box-shadow: 0 0 0 3px var(--banana-300);
}

/* ── Error ─────────────────────────────────────────── */
.error {
  font-family: var(--font-body);
  font-weight: 600;
  color: var(--bruise);
  background: color-mix(in srgb, var(--bruise) 12%, white);
  border: 2px solid var(--bruise);
  border-radius: var(--r-md);
  padding: 10px 14px;
  margin: 0;
}

/* ── Success ───────────────────────────────────────── */
.done {
  position: relative;
  max-width: 640px;
  margin: 20px auto 0;
  text-align: center;
  overflow: hidden;
}
.done__badge { font-size: 3.5rem; }
.done__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(1.8rem, 5vw, 2.6rem);
  color: var(--ink-900);
  margin: 8px 0 8px;
}
.done__lead { font-family: var(--font-accent); font-style: italic; color: var(--ink-500); font-size: 1.15rem; margin: 0 0 26px; }
.done__card { text-align: left; margin: 0 auto 28px; }
.done__actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

/* ── Banana rain ───────────────────────────────────── */
.rain { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
.rain span {
  position: absolute;
  top: -10%;
  animation-name: fall;
  animation-timing-function: linear;
  animation-iteration-count: 1;
  opacity: 0.9;
}
@keyframes fall {
  to { transform: translateY(120vh) rotate(360deg); opacity: 0; }
}
@keyframes swing {
  0%, 100% { transform: rotate(-8deg); }
  50% { transform: rotate(8deg); }
}
.done > :not(.rain) { position: relative; z-index: 1; }

@media (max-width: 640px) {
  .text-row { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .submit { padding: 32px 20px 60px; }
  .gate { padding: 36px 22px; }
  .card { padding: 22px 18px 24px; gap: 22px; }
  .pills { gap: 8px; }
  .pill { font-size: 0.88rem; padding: 7px 12px; }
  .done__actions { gap: 10px; }
}
</style>
